import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, runBootstrap, useApi } from './client'
import type { Invoice, InvoiceReceivableSummary, InvoiceStatus } from './types'

const ROOT = '/invoices'
const KEY = ['invoices'] as const

type ListResp = { items: Invoice[]; nextCursor: string | null }

export type InvoicesFilter = {
  organizationId?: string
  counterpartyId?: string
  status?: InvoiceStatus
  dateFrom?: string
  dateTo?: string
}

export function useInvoices(filter: InvoicesFilter = {}) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: [...KEY, 'list', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api<ListResp>(
        `${ROOT}${buildQuery({
          organization_id: filter.organizationId,
          counterparty_id: filter.counterpartyId,
          status: filter.status,
          date_from: filter.dateFrom,
          date_to: filter.dateTo,
          cursor: pageParam,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
}

export function flattenInvoices(data: { pages?: ListResp[] } | undefined): Invoice[] {
  if (!data?.pages) return []
  return data.pages.flatMap((p) => p.items)
}

// Aggregate "what clients still owe us" across unpaid invoices. Honors the same
// org/counterparty filters as the list so the banner matches the view.
export function useInvoiceReceivableSummary(
  filter: Pick<InvoicesFilter, 'organizationId' | 'counterpartyId'> = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'receivable-summary', filter],
    queryFn: () =>
      api<InvoiceReceivableSummary>(
        `${ROOT}/receivable-summary${buildQuery({
          organization_id: filter.organizationId,
          counterparty_id: filter.counterpartyId,
        })}`,
      ),
  })
}

export type RecordPaymentInput = {
  invoiceId: string
  amount: number
  accountId: string
  categoryId: string
  date: string
  description?: string
}

export type RecordPaymentResult = {
  transactionId: string
  invoicePaymentId: string
  paidAmount: number
  status: InvoiceStatus
}

export function useRecordInvoicePayment() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RecordPaymentInput) =>
      api<RecordPaymentResult>(`${ROOT}/${input.invoiceId}/payments`, {
        method: 'POST',
        body: {
          amount: input.amount,
          accountId: input.accountId,
          categoryId: input.categoryId,
          date: input.date,
          description: input.description ?? null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['transactions'] })
      qc.invalidateQueries({ queryKey: ['accounts'] })
    },
  })
}

/**
 * One user action, two passes. The import pass pulls open invoices from B24; the
 * reconcile pass then re-reads the invoices we still hold as open and books any
 * payment B24 already reports. Without the second pass a paid transition whose
 * event was lost (B24 outage / deactivated handler) can never be recovered from
 * the UI: paid invoices are out of the import's scope by design.
 */
export function useBootstrapInvoices() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const imported = await runBootstrap(api, '/b24/bootstrap-invoices')
      const reconcile = await runBootstrap(api, '/b24/reconcile-invoices')
      return { ...imported, reconcile }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['transactions'] })
      qc.invalidateQueries({ queryKey: ['accounts'] })
    },
  })
}
